import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import i18n from 'modules/core/lib/i18n'
import DataTable from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable'
import useFetchPlanningBalances from 'modules/shift_management/components/ShiftManagement/Content/useFetchPlanningBalances'
import useGetShiftsRows from 'modules/shift_management/components/ShiftManagement/MainPage/useGetShiftRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'

/**
 * Walk from `element` up to `document.body`, saving and overriding
 * every ancestor's height/overflow/position styles so the browser's
 * print engine can paginate the content across multiple pages.
 *
 * Returns a cleanup function that restores every touched style.
 */
function unlockAncestorsForPrint(element: HTMLElement): () => void {
  const saved: { el: HTMLElement; props: Record<string, string> }[] = []

  let current: HTMLElement | null = element.parentElement
  while (current && current !== document.documentElement) {
    const style = current.style
    const backup: Record<string, string> = {
      height: style.height,
      maxHeight: style.maxHeight,
      overflow: style.overflow,
      overflowX: style.overflowX,
      overflowY: style.overflowY,
      position: style.position,
      display: style.display,
      padding: style.padding,
      margin: style.margin,
    }
    saved.push({ el: current, props: backup })

    style.setProperty('height', 'auto', 'important')
    style.setProperty('max-height', 'none', 'important')
    style.setProperty('overflow', 'visible', 'important')
    style.setProperty('overflow-x', 'visible', 'important')
    style.setProperty('overflow-y', 'visible', 'important')
    style.setProperty('padding', '0', 'important')
    style.setProperty('margin', '0', 'important')

    current = current.parentElement
  }

  // Also unlock html element
  const html = document.documentElement
  const htmlBackup = {
    height: html.style.height,
    overflow: html.style.overflow,
  }
  html.style.setProperty('height', 'auto', 'important')
  html.style.setProperty('overflow', 'visible', 'important')

  return () => {
    for (const { el, props } of saved) {
      for (const [key, value] of Object.entries(props)) {
        if (value) {
          el.style.setProperty(key.replace(/([A-Z])/g, '-$1').toLowerCase(), value)
        } else {
          el.style.removeProperty(key.replace(/([A-Z])/g, '-$1').toLowerCase())
        }
      }
    }
    if (htmlBackup.height) {
      html.style.height = htmlBackup.height
    } else {
      html.style.removeProperty('height')
    }
    if (htmlBackup.overflow) {
      html.style.overflow = htmlBackup.overflow
    } else {
      html.style.removeProperty('overflow')
    }
  }
}

/**
 * Hide all siblings of each ancestor (sidebar, nav, etc.) so only the
 * print content's ancestry chain is visible. Returns a cleanup function.
 */
function hideSiblings(element: HTMLElement): () => void {
  const hidden: { el: HTMLElement; prev: string }[] = []

  let current: HTMLElement | null = element
  while (current && current !== document.body) {
    const parent: HTMLElement | null = current.parentElement
    if (parent) {
      for (let i = 0; i < parent.children.length; i++) {
        const sibling = parent.children[i]
        if (!(sibling instanceof HTMLElement)) continue
        if (sibling !== current && sibling.style.display !== 'none') {
          hidden.push({ el: sibling, prev: sibling.style.display })
          sibling.style.setProperty('display', 'none', 'important')
        }
      }
    }
    current = parent
  }

  return () => {
    for (const { el, prev } of hidden) {
      if (prev) {
        el.style.display = prev
      } else {
        el.style.removeProperty('display')
      }
    }
  }
}

const PRINT_CSS = `
@media print {
  .print-schedule-loading {
    display: none !important;
  }
  .print-schedule-header {
    padding: 12px 8px 8px !important;
    font-size: 16px;
    font-weight: 700;
    color: #212529;
  }
  /* Un-sticky the table header so it flows with content */
  .print-schedule-root [class*="sticky"] {
    position: static !important;
  }
  /* Hide UI-only interactive elements */
  .print-schedule-root [style*='translateY(30%)'] { display: none !important; }
  .print-schedule-root span[role="img"][aria-hidden="true"],
  .print-schedule-root span.relative.flex.rounded-full,
  .print-schedule-root img,
  .print-schedule-root div[role="checkbox"],
  .print-schedule-root button:last-of-type {
    display: none !important;
  }
  .print-schedule-root [class*="ShiftCellActions_relative"] > [style*="right"] {
    display: none !important;
  }
  .print-schedule-root [class*="ShiftCellActions_relative"] {
    overflow: hidden !important;
  }
  @page { margin: 0.5cm; size: landscape; }
}
`

const PrintSchedule = () => {
  const navigate = useNavigate()
  const [{ view }] = useSchedule()
  const rootRef = useRef<HTMLDivElement>(null)
  const [isRendered, setIsRendered] = useState(false)

  const { shiftsRows: allEmployeesRows, loading: allEmployeesLoading } = useGetShiftsRows({
    printSchedule: true,
  })
  const [{ shiftsRows: contextShiftsRows }] = useShiftsRows()
  const { balancesByEmployee, periodBalancesByEmployee } = useFetchPlanningBalances()
  const hasPrinted = useRef(false)

  const shiftsRows = view.isByEmployees ? allEmployeesRows : contextShiftsRows
  const loading = view.isByEmployees ? allEmployeesLoading : false

  const isDataReady = view.isByEmployees
    ? !loading && allEmployeesRows.length > 0
    : contextShiftsRows.length > 0

  // Wait for the DataTable to fully paint before triggering print
  useEffect(() => {
    if (!isDataReady) return

    let innerRafId: number
    const outerRafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => {
        setIsRendered(true)
      })
    })

    return () => {
      cancelAnimationFrame(outerRafId)
      cancelAnimationFrame(innerRafId)
    }
  }, [isDataReady])

  useEffect(() => {
    if (isRendered && !hasPrinted.current) {
      hasPrinted.current = true
      // Small delay to ensure the DOM is fully painted before print
      setTimeout(() => {
        if (!rootRef.current) return

        // Apply fit-to-page scaling using the browser's viewport width as a proxy
        // for printable area. The @page rule requests landscape orientation.
        const contentWidth = rootRef.current.scrollWidth
        const availableWidth = window.innerWidth
        if (contentWidth > availableWidth) {
          const scale = availableWidth / contentWidth
          rootRef.current.style.setProperty('zoom', `${scale}`)
        }

        // Walk up the DOM and clear all height/overflow constraints
        // so the browser can paginate the content across print pages.
        // Also hide all sibling elements (sidebar, nav, etc.)
        const restoreAncestors = unlockAncestorsForPrint(rootRef.current)
        const restoreSiblings = hideSiblings(rootRef.current)

        // Single afterprint handler: restore styles first, then navigate back.
        // Merging into one handler avoids ordering issues between cleanup and navigation.
        const onAfterPrint = () => {
          restoreSiblings()
          restoreAncestors()
          window.removeEventListener('afterprint', onAfterPrint)
          navigate(-1)
        }
        window.addEventListener('afterprint', onAfterPrint)

        window.print()
      }, 500)
    }
  }, [isRendered, navigate])

  return (
    <>
      <style>{PRINT_CSS}</style>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {!isRendered && (
        <div
          className="print-schedule-loading"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            zIndex: 9999,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 40,
                height: 40,
                border: '4px solid #e9ecef',
                borderTop: '4px solid #868e96',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 12px',
              }}
            />
            <p style={{ color: '#868e96', fontSize: 14 }}>
              {i18n.t('shift_management.actions.print_shifts.loading_employees', {
                count: shiftsRows.length,
                ellipsis: loading ? '...' : '',
              })}
            </p>
          </div>
        </div>
      )}

      <div className="print-schedule-print-wrapper">
        <div className="print-schedule-root" ref={rootRef}>
          <div className="print-schedule-header">{view.headerTitle}</div>
          <DataTable
            view={view}
            shiftsRows={shiftsRows}
            balancesByEmployee={balancesByEmployee}
            periodBalancesByEmployee={periodBalancesByEmployee}
            disableVirtualization
          />
        </div>
      </div>
    </>
  )
}

export default PrintSchedule
